import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { entitlementsApi } from '@/services/api';
import {
  isDevLoadingTraceEnabled,
  useDevLoadingTrace,
} from '@/utils/devLoadingTrace';

interface Entitlement {
  plan?: string;
  status?: string;
  [key: string]: unknown;
}

interface UseEntitlementResult {
  entitlement: Entitlement | null;
  plan: string;
  isFree: boolean;
  isActive: boolean;
  loading: boolean;
  error: Error | null;
}

type EntitlementTracePhase =
  | 'idle'
  | 'waiting-role'
  | 'fetching-token'
  | 'fetching-entitlement';

export function useEntitlement(): UseEntitlementResult {
  const { getAccessToken, isAuthenticated, isRoleLoaded, user, userStatus } =
    useAuth();
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tracePhase, setTracePhase] = useState<EntitlementTracePhase>('idle');
  const userId = user?.sub ?? null;
  const setEnabledTracePhase = useCallback((phase: EntitlementTracePhase) => {
    if (isDevLoadingTraceEnabled()) setTracePhase(phase);
  }, []);

  useDevLoadingTrace(
    'auth.entitlement-token-fetch',
    tracePhase === 'fetching-token',
    {
      tokenFetchActive: tracePhase === 'fetching-token',
      entitlementLoading: loading,
      isAuthenticated,
      isRoleLoaded,
      userId,
      userStatus,
      hasError: Boolean(error),
      tracePhase,
    }
  );

  useDevLoadingTrace(
    'auth.entitlement-api-fetch',
    tracePhase === 'fetching-entitlement',
    {
      entitlementFetchActive: tracePhase === 'fetching-entitlement',
      entitlementLoading: loading,
      isAuthenticated,
      isRoleLoaded,
      userId,
      userStatus,
      hasEntitlement: Boolean(entitlement),
      hasError: Boolean(error),
      tracePhase,
    }
  );

  useEffect(() => {
    let mounted = true;

    if (!isAuthenticated) {
      setEnabledTracePhase('idle');
      setEntitlement(null);
      setError(null);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    if (!isRoleLoaded) {
      setEnabledTracePhase('waiting-role');
      setLoading(true);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);
        setEnabledTracePhase('fetching-token');
        const token = await getAccessToken();
        if (mounted) setEnabledTracePhase('fetching-entitlement');
        const e = await entitlementsApi.getMyEntitlement(token);
        if (mounted) setEntitlement(e);
      } catch (err) {
        if (mounted) {
          setEntitlement(null);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setEnabledTracePhase('idle');
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [
    getAccessToken,
    isAuthenticated,
    isRoleLoaded,
    setEnabledTracePhase,
    userId,
    userStatus,
  ]);

  const plan = entitlement?.plan || 'free';
  const isFree = plan === 'free';
  const isActive = entitlement?.status === 'active';

  return { entitlement, plan, isFree, isActive, loading, error };
}
